import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import { getSelectedStoreTenant } from '../selectors/tenant.selectors';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';
import * as NotificationsActions from '../../store/actions/notifications.actions';
import * as NotificationsSelectors from '../../store/selectors/notifications.selectors';
import { LaravelNotificationsService } from '../../commons/services/laravel/laravel-notifications.service';
import { NewNotificationsComponent } from '../../modules/home/modules/notifications/new-notification/new-notifications.component';


@Injectable()
export class NotificationEffects {

    error$ = createEffect(
        () =>
            this.actions$.pipe(
            ofType(
                NotificationsActions.deleteNotificationFailed,
                NotificationsActions.saveNotificationFailed
            ),
            tap(({ error }) => {
                if (error) {
                this.alertService.showErrorMessage('Errore', error);
                }
            })
            ),
        { dispatch: false }
    );
    
    loadNotifications$ = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.loadNotifications),
        withLatestFrom(this.store$.select(getSelectedStoreTenant)),
        map(([{ page, perPage, order, direction, filters, includes }, tenant]) => {
            const filter = {...filters};
            if(filters.onlyCurrentTenant){
            filter.tenant_id = tenant.id
            }
            return {page, perPage, order, direction, filters: filter, includes};
        }),
        switchMap(({page, perPage, order, direction, filters, includes}) => {
            return this.notificationsService
            .list(page, perPage, order, direction, filters, includes)
            .pipe(
                map((result) => {
                return NotificationsActions.loadNotificationsCompleted({
                    notifications: result.data,
                    currentPage: page,
                    total: result.total,
                    perPage,
                    order,
                    direction,
                    filters,
                    includes
                    });
                }),
                catchError((error) =>
                of(NotificationsActions.loadNotificationsFailed({ error })))
            );
            })
            )
    );
    
    changePage = createEffect(() =>
    this.actions$.pipe(
        ofType(NotificationsActions.changePage),
        withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationsTableState)),
        map(
        ([
            { page, size },
            { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
            NotificationsActions.loadNotifications({
            page: page,
            perPage: size,
            order,
            direction,
            filters,
            includes,
            })
        )
    )
    );
        
    changeSort = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.changeSort),
        withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationsTableState)),
        map(
            ([
            action,
            { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                NotificationsActions.loadNotifications({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
            })
        )
        )
    );
        
    changeFilters = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.changeFilters),
        withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationsTableState)),
        map(
            ([
            { filters },
            { total, currentPage, perPage, direction, order, includes },
            ]) =>
                NotificationsActions.loadNotifications({
                page: currentPage,
                perPage: perPage,
                order,
                direction,
                filters,
                includes,
            })
        )
        )
    );
    
    editNotification$ = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.editNotification),
        map(({ notification }) => {
            let dialogRef = this.dialog.open(NewNotificationsComponent, {
            data: {
                notification,
            },
            panelClass: 'full-screen-dialog',
            });
            return NotificationsActions.notificationDialogOpened({
            dialogId: dialogRef.id,
            });
        })
        )
    );
    
    saveNotification$ = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.saveNotification),
        withLatestFrom(this.store$.select(getSelectedStoreTenant)),
        mergeMap(([{ notification }, selectedTenant]) => {
            let tenantId = selectedTenant ? selectedTenant.id : null;
            let notificationSave = {
            ...notification.toDTO(),
            tenant_id: tenantId
            };
            return this.notificationsService.upsert(notificationSave).pipe(
                map((result) =>
                    NotificationsActions.saveNotificationCompleted({ notification: result })
                ),
            tap((action) => {
                if (action.type === NotificationsActions.saveNotificationCompleted.type) {
                this.alertService.showConfirmMessage(`Notifica salvata con successo`)
                this.store$.dispatch(NotificationsActions.closeNotificationsDialog());
                } else {
                this.store$.dispatch(NotificationsActions.saveNotificationFailed({ error: Error }));
                }
            })
            );
        })
        )
    );
    
    deleteNotification$ = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.deleteNotification),
        switchMap(({ notification }) =>
            this.alertService
            .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare l'elemento selezionato?`
            )
            .pipe(
                mergeMap((confirm) => {
                return confirm
                    ? this.notificationsService.delete(notification.id).pipe(
                        map(() =>
                        NotificationsActions.deleteNotificationCompleted({
                            notification,
                        })
                        ),
                        catchError((error) =>
                        of(NotificationsActions.deleteNotificationFailed({ error }))
                        ),
                        tap((action) => {
                        if (action.type === NotificationsActions.deleteNotificationCompleted.type) {
                            this.alertService.showConfirmMessage(` Notifica eliminata con successo`);
                            this.store$.dispatch(NotificationsActions.closeNotificationsDialog());
                        }
                        })
                    )
                    : of(NotificationsActions.deleteNotificationCancelled());
                })
            )
        )
        )
    );
    
    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
        ofType(NotificationsActions.saveNotificationCompleted),
        withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationsTableState)),
        map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                NotificationsActions.loadNotifications({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
            })
        )
        )
    );
            
    reloadAfterDelete = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.deleteNotificationCompleted),
            withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationsTableState)),
            map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                NotificationsActions.loadNotifications({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
                })
            )
        )
    );
        
        
    closeSelectionDialog = createEffect(
        () =>
            this.actions$.pipe(
            ofType(NotificationsActions.closeSelectionDialog),
            withLatestFrom(this.store$.select(NotificationsSelectors.getSelectionDialogId)),
            tap(([_, dialogId]) => {
                if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
                }
            })
            ),
        { dispatch: false }
    );
            
    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.requestCloseNotificationsDialog),
            switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
                disableClose: false
            });

            // Imposta il messaggio del dialog
            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
                "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

            return confirmDialogRef.afterClosed().pipe(
                map(result =>
                result
                    ? NotificationsActions.confirmCloseNotificationsDialog()
                    : NotificationsActions.cancelCloseNotificationsDialog()
                )
            );
            })
        )
    );
            
            
    confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.confirmCloseNotificationsDialog),
            map(() => NotificationsActions.closeNotificationsDialog())
        )
    );

    cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.cancelCloseNotificationsDialog),
            map(() => {
            return { type: 'NO_ACTION' };
            })
        )
    );
            
    closeDialog$ = createEffect(
        () =>
            this.actions$.pipe(
            ofType(NotificationsActions.closeNotificationsDialog),
            withLatestFrom(this.store$.select(NotificationsSelectors.getNotificationDialogId)),
            tap(([_, dialogId]) => {
                if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
                }
            })
            ),
        { dispatch: false }
    );


    constructor(
            private actions$: Actions,
            private store$: Store<AppState>,
            private notificationsService: LaravelNotificationsService,
            private dialog: MatDialog,
            private alertService: AlertService
    ) {}
}